import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  fetchApiGet,
  fetchApiPut,
  fetchApiPost,
  fetchApiDelete,
} from "../utils";
import { BaseDbObject } from "@/lib/models/db";
import { zodStrictRequiredDecimal } from "../common";
import { DbUser } from "@/lib/models";

export enum FieldType {
  System = "system",
  Signature = "signature",
}

export enum SignatureFieldType {
  Signature = "signature",
  SignatureInitials = "initials",
  Date = "date_signed",
  Checkbox = "checkbox",
  TextArea = "note",
  Text = "text",
  Attachment = "attachment",
}

export enum SystemFieldType {
  SystemReferenceField = 'system_reference_field',
  EnumFixedValue = 'enum_fixed_value',
  FreeformFixed = 'freeform_fixed',
  CreationManualInput = 'creation_manual_input',
}

export const ContractTemplatePositionData = z.object({
  x: zodStrictRequiredDecimal({ key: "x" }),
  y: zodStrictRequiredDecimal({ key: "y" }),
  width: zodStrictRequiredDecimal({ key: "width" }),
  height: zodStrictRequiredDecimal({ key: "height" }),
  page: z.number().min(1),
  text_size: z.number().min(1).max(20),
});

export const ContractTemplateFields = z.object({
  field_type: z.enum([FieldType.System, FieldType.Signature]),
  field_id: z.string(),
  field_key: z.string(),

  system_field_type: z.nativeEnum(SystemFieldType).optional(),
  field_dest_type: z.enum([
    SignatureFieldType.Signature, SignatureFieldType.SignatureInitials,
    SignatureFieldType.Date, SignatureFieldType.Checkbox,
    SignatureFieldType.Text, SignatureFieldType.Attachment,
    SignatureFieldType.TextArea
  ]).optional(),
  position_data: ContractTemplatePositionData,

  signer_id: z.string().optional(),
  is_required: z.boolean(),
  is_readonly: z.boolean(),

  freeform_value: z.string().optional(),
}).refine((data) => {
  if (data.field_type === FieldType.Signature && !data.field_dest_type) {
    return false;
  }
  return true;
});

export const CreateContractTemplateFieldsValidation = z.array(ContractTemplateFields);

export const CreateContractTemplateValidation = z.object({
  version_label: z.string().nullable().optional(),
  version_reason: z.string().nullable().optional(),

  require_all_signers: z.boolean(),
  send_auto_reminders: z.boolean(),
  use_signer_order: z.boolean(),

  name: z.string().optional(),

  fields: CreateContractTemplateFieldsValidation
});

export interface DocumentPositionData {
  x: string;
  y: string;
  width: string;
  height: string;
  page: number;
  text_size: number;
}

export interface EverSignSessionEvent { }
export interface EverSignInstanceMetaData { }
export interface DocumentInstanceMetaData {
  contract_number?: string;
  contract_type?: string;
  contract_quantity?: string;
  contract_shipping_dates?: string;
  contract_date?: string;
}

export interface DocumentTemplate extends BaseDbObject {
  parent_group_id: string;
  document_id: string;
  created_by_id: string;

  template_content_type: string;

  version_number: number;
  version_label?: string;
  version_reason?: string;

  require_all_signers: boolean;
  send_auto_reminders: boolean;
  use_signer_order: boolean;

  title?: string;

  deleted_at?: Date;

  Document?: Document;
  CreatedBy?: DbUser;
  Fields?: DocumentTemplateField[];
  Instances?: DocumentTemplateInstance[];
}

export interface DocumentTemplateField extends BaseDbObject {
  document_template_id: string;

  field_type: string;
  field_id: string;
  field_key: string;

  field_dest_type?: string;
  position_data: DocumentPositionData; // Prisma's Json type

  signer_id?: string;
  is_required: boolean;
  is_readonly: boolean;

  DocumentTemplate?: DocumentTemplate;
}

export interface DocumentTemplateInstance extends BaseDbObject {
  document_template_id: string;
  signing_session_id: string;
  parent_instance_id: string;

  document_title: string;
  feature_type: string;

  title?: string;

  meta_data: DocumentInstanceMetaData;

  DocumentTemplate?: DocumentTemplate;
  SigningSession?: DocumentSigningSession;
}

export interface DocumentSigningSession extends BaseDbObject {
  provider_session_id: string;
  provider_type: string;

  is_sandbox: boolean;
  is_draft: boolean;
  meta_data: EverSignInstanceMetaData;

  final_document_id?: string;
  sent_document_id?: string;

  last_status: string;
  archived_at?: Date;
  deleted_at?: Date;
  trashed_at?: Date;
  cancelled_at?: Date;
  revoked_at?: Date;
  declined_at?: Date;
  completed_at?: Date;
  expired_at?: Date;

  TemplateInstances?: DocumentTemplateInstance[];
  SigningRecipients?: DocumentSessionRecipient[];
  Events?: DocumentSessionEvent[];
}

export interface DocumentSessionRecipient extends BaseDbObject {
  signing_session_id: string;
  customer_id?: string;

  recipient_type: string;
  name: string;
  email: string;

  signer_id?: string;
  signing_order: number;

  last_status: string;
  signed_at?: Date;
  declined_at?: Date;
  removed_at?: Date;
  bounced_at?: Date;
  viewed_at?: Date;

  SigningSession?: DocumentSigningSession;
  Customer?: { id: string; long_name: string };
  Events?: DocumentSessionEvent[];
}

export interface DocumentSessionEvent extends BaseDbObject {
  signing_session_id: string;
  session_recipient_id?: string;

  event_type: string;
  meta_data: EverSignSessionEvent; // Prisma's Json type

  SigningSession?: DocumentSigningSession;
  SigningRecipient?: DocumentSessionRecipient;
}

export enum DocumentSigningStatus {
  Uploading = 'uploading',
  Sent = 'sent',
  Archived = 'archived',
  Deleted = 'deleted',
  Trashed = 'trashed',
  Cancelled = 'cancelled',
  Revoked = 'revoked',
  Declined = 'declined',
  Completed = 'completed',
  Expired = 'expired',
}


export const DocumentSigningStatusOptions = [
  { value: DocumentSigningStatus.Uploading, label: 'Uploading' },
  { value: DocumentSigningStatus.Sent, label: 'Sent' },
  { value: DocumentSigningStatus.Archived, label: 'Archived' },
  { value: DocumentSigningStatus.Deleted, label: 'Deleted' },
  { value: DocumentSigningStatus.Trashed, label: 'Trashed' },
  { value: DocumentSigningStatus.Cancelled, label: 'Cancelled' },
  { value: DocumentSigningStatus.Revoked, label: 'Revoked' },
  { value: DocumentSigningStatus.Declined, label: 'Declined' },
  { value: DocumentSigningStatus.Completed, label: 'Completed' },
  { value: DocumentSigningStatus.Expired, label: 'Expired' },
];

export function getSigningStatusLabel(status: string) {
  if (!status) return '';
  return DocumentSigningStatusOptions.find(item => item.value === status)?.label || status;
}

export interface EverSignSessionEvent { }
export interface EverSignInstanceMetaData { }
export interface DocumentInstanceMetaData { }
